import React, { useState, useEffect, useMemo } from 'react';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { OrderSearchOptions, searchOrderGroups, getSingleUnitOrderRoomFormExportUrl } from '@/services/orders';
import { useDefaultScrollToId, useMergeState, useEffectSkipFirst } from '@/utils/hooks';
import { OrderGroup, OrderStatus } from '@/types';
import { Trans } from '@lingui/react/macro';
import { ExternalLinkButton } from '@/components/Buttons';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import OrderGroupList from '@/seller/components/orders/OrderGroupList';
import OrderExportSelectSketchDialog from '@/seller/components/orders/OrderExportSelectSketchDialog';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { getInvoiceExportUrl } from '@/services/order-export';
import { Button, ButtonBar, SlideToggle, Tabs } from '@ygtechnologies/rubus-ui';

type OrderToggleState = 'all' | 'options' | 'change-requests';
import { OrderSortingDropdown } from '@/seller/components/orders/OrderSortingDropdown';
import { useOrderSorting } from '@/seller/components/orders/hooks';

type ResidentialUnitOrdersProps = {
    projectId: number;
    residentialUnitId: number;
};

const ResidentialUnitOrders: React.FC<ResidentialUnitOrdersProps> = ({ projectId, residentialUnitId }) => {
    const [orderGroups, setOrderGroups] = useState<OrderGroup[] | null>(null);
    const defaultExpandedId = useDefaultScrollToId();
    const [toggleState, setToggleState] = useState<OrderToggleState>('all');
    const [showRoomFormExportDialog, setShowRoomFormExportDialog] = useState(false);
    const project = useSelector((state: IState) => state.global.activeProject!);
    const [showCancelled, setShowCancelled] = useState(false);

    // Use the shared sorting hook
    const { sortDefinitions, currentSort, setCurrentSort, currentSortDefinition, applySorting } = useOrderSorting();

    const filters = useMemo<OrderSearchOptions>(
        () => ({
            residentialUnitIds: [residentialUnitId],
            status: showCancelled ? [] : [OrderStatus.ReadyToStart],
        }),
        [residentialUnitId, showCancelled]
    );

    useEffect(() => {
        searchOrderGroups(filters).then(setOrderGroups);
    }, [filters]);

    useEffect(() => {
        if (defaultExpandedId && orderGroups !== null) {
            var order = orderGroups.find((x) => x.orders.find((y) => y.id === defaultExpandedId));
            if (order && order.deadlineName) {
                setToggleState('options');
            } else if (order && order.changeRequestName) {
                setToggleState('change-requests');
            }
        }
    }, [orderGroups, defaultExpandedId]);

    // Compute visible orders with sorting using useMemo instead of useEffect
    const visibleOrders = useMemo(() => {
        function getCurrentOrders() {
            switch (toggleState) {
                case 'options':
                    return orderGroups?.filter((x) => x.deadlineName);
                case 'change-requests':
                    return orderGroups?.filter((x) => x.changeRequestName);
                default:
                    return orderGroups;
            }
        }

        var newVisibleOrders = getCurrentOrders();

        // Apply sorting using the hook
        if (newVisibleOrders) {
            newVisibleOrders = applySorting(newVisibleOrders);
        }

        return newVisibleOrders || null;
    }, [toggleState, orderGroups, applySorting]);
    const showInvoiceExport = project.permissions.canViewSensitiveData;

    return (
        <React.Fragment>
            <ButtonBar sticky margins="none">
                <Tabs
                    activeId={toggleState}
                    onChange={(id) => setToggleState(id as OrderToggleState)}
                    variant="pill"
                    size="sm">
                    <Tabs.Tab id="all" label={<Trans>All</Trans>} />
                    <Tabs.Tab id="options" label={<Trans>Option orders</Trans>} />
                    <Tabs.Tab id="change-requests" label={<Trans>Change request orders</Trans>} />
                </Tabs>

                {/* <ExternalLinkButton
                    href={getSingleUnitOrderRoomFormExportUrl(residentialUnitId)}
                    color="tertiary"
                    icon={faDownload}>
                    <Trans>Export room form</Trans>
                </ExternalLinkButton> */}
                <ButtonBar.Right>
                    <OrderSortingDropdown
                        currentSort={currentSort}
                        sortDefinitions={sortDefinitions}
                        setCurrentSort={setCurrentSort}
                        currentSortDefinition={currentSortDefinition}
                    />
                    <SlideToggle onChange={(e) => setShowCancelled(e.target.checked)} checked={showCancelled}>
                        <Trans>Show cancelled orders</Trans>
                    </SlideToggle>
                    {showInvoiceExport && (
                        <Button
                            as="a"
                            variant="tertiary"
                            href={getInvoiceExportUrl({ projectId, residentialUnitIds: [residentialUnitId] }, null)}
                            icon={faDownload}>
                            <Trans>Invoice export</Trans>
                        </Button>
                    )}

                    <Button variant="tertiary" onClick={() => setShowRoomFormExportDialog(true)} icon={faDownload}>
                        <Trans>Export room form</Trans>
                    </Button>
                </ButtonBar.Right>
            </ButtonBar>

            {visibleOrders ? (
                <OrderGroupList
                    onChange={(changes) => {
                        orderGroups?.forEach((x) => (x.orders = changes.updateArray(x.orders)));
                        orderGroups && setOrderGroups([...orderGroups]);
                    }}
                    defaultExpandedId={defaultExpandedId}
                    orders={visibleOrders}
                />
            ) : (
                <LoadingIndicator />
            )}

            <OrderExportSelectSketchDialog
                show={showRoomFormExportDialog}
                onClose={() => setShowRoomFormExportDialog(false)}
                onAccept={(includeSketch, documentVersionId) => {
                    setShowRoomFormExportDialog(false);
                    window.open(getSingleUnitOrderRoomFormExportUrl(residentialUnitId, includeSketch, documentVersionId));
                }}
                residentialUnitId={residentialUnitId}
            />
        </React.Fragment>
    );
};

export default ResidentialUnitOrders;
